<template>
  <slot />
</template>

<script setup lang="ts">
  // Widgets

  // Features

  // Entities

  // Shared

  // External and shared packages
  import { useHead } from '@unhead/vue';
  import { computed } from 'vue';
  import { addMinutes, formatDate } from 'date-fns';
  import type { Experience } from '@experience/exp-card/dist/types/experience';

  interface Props {
    exp: Experience;
    isShowSchemaOrg: boolean;
  }

  const props = defineProps<Props>();

  const startDate = computed(() => {
    const firstScheduleItem = props.exp.schedule?.upcoming_events?.[0];
    const date = firstScheduleItem ? new Date(firstScheduleItem) : new Date();

    return formatDate(date, "yyyy-MM-dd'T'HH:mm");
  });

  const endDate = computed(() => {
    const firstScheduleItem = props.exp.schedule?.upcoming_events?.[0];
    if (firstScheduleItem) {
      const duration = (props.exp?.duration || 0) * 60;
      const timeEnd = addMinutes(new Date(firstScheduleItem), duration);

      return formatDate(timeEnd, "yyyy-MM-dd'T'kk:mm");
    }

    return undefined;
  });

  const meta = computed(() => {
    return {
      '@context': 'http://schema.org',
      '@type': 'Event',
      name: `🧭 ${props.exp.title}`,
      startDate: startDate.value,
      endDate: endDate.value,
      url: props.exp.url,
      image: props.exp.photos?.[0]?.medium || '',
      description: props.exp.annotation,
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      eventStatus: 'http://schema.org/EventScheduled',
      organizer: {
        '@type': 'Person',
        name: props.exp.guide?.first_name || '',
        url: props.exp.guide?.url || '',
      },
      performer: {
        '@type': 'PerformingGroup',
        name: props.exp.guide?.first_name || '',
        url: props.exp.guide?.url || '',
      },
      location: {
        '@type': 'Place',
        name: props.exp.geo?.city?.[0]?.slug || '',
        url: props.exp.geo?.city?.[0]?.slug
          ? `https://experience.tripster.ru/experience/${props.exp.geo?.city?.[0]?.slug}/`
          : '',
        address: [
          {
            '@type': 'PostalAddress',
            addressLocality: props.exp.geo?.city?.[0]?.name || '',
            addressCountry: [
              {
                '@type': 'Country',
                name: props.exp.geo?.country?.[0]?.name || '',
              },
            ],
          },
        ],
      },
      offers: {
        '@type': 'Offer',
        availability: 'http://schema.org/InStock ',
        validFrom: startDate.value,
        url: props.exp.url,
        price: props.exp.price.value,
        priceCurrency: props.exp.price.currency,
      },
    };
  });

  const scriptConfig = computed(() => {
    return props.isShowSchemaOrg
      ? [
          {
            type: 'application/ld+json',
            innerHTML: computed(() => JSON.stringify(meta.value)),
            key: `schema-exp-${props.exp.id}`,
          },
        ]
      : null;
  });

  useHead({
    script: computed(() => scriptConfig.value),
  });
</script>

<style scoped lang="scss"></style>
